

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getQueryForm,
  updateQueryForm,
} from "../../services/queryformService";
import { toast } from "react-toastify";

const initialState = {
  queries: [],
  status: "idle",
  error: null,
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  }
};

// Fetch all queries
export const fetchQueries = createAsyncThunk(
  "queries/fetchQueries",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await getQueryForm(params);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to fetch queries";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Update a query
export const updateQuery = createAsyncThunk(
  "queries/updateQuery",
  async ({ id, queryData }, { rejectWithValue }) => {
    try {
      const response = await updateQueryForm(id, queryData);
      toast.success(response.message || "Query updated successfully!");
      return { id, updatedData: response.data || response, queryData };
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to update query";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const queryFormSlice = createSlice({
  name: "queries",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetQueryState: (state) => {
      state.queries = [];
      state.status = "idle";
      state.error = null;
      state.pagination = {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0
      };
    },
    setQueries: (state, action) => {
      state.queries = action.payload;
    },
    // Add this new reducer to manually update a query
    updateQueryInState: (state, action) => {
      const { id, updates } = action.payload;
      const index = state.queries.findIndex(query => query._id === id);
      if (index !== -1) {
        state.queries[index] = { ...state.queries[index], ...updates };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Queries
      .addCase(fetchQueries.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      // .addCase(fetchQueries.fulfilled, (state, action) => {
      //   state.status = "succeeded";
      //   state.queries = action.payload.queries || [];
      //   state.pagination = action.payload.pagination || {
      //     total: state.queries.length,
      //     page: 1,
      //     limit: 10,
      //     totalPages: 1
      //   };
      //   state.error = null;
      // })

// In your queryformSlice.js - update the fetchQueries.fulfilled case
.addCase(fetchQueries.fulfilled, (state, action) => {
  state.status = "succeeded";
  
  // Ensure queries is always an array and normalize the data
  const rawQueries = action.payload.queries || [];
  state.queries = Array.isArray(rawQueries) ? rawQueries.map(query => ({
    ...query,
    // Normalize the query field to always be a string
    query: typeof query.query === 'string' ? query.query : 
           (query.query?.query || query.query?.text || JSON.stringify(query.query || ''))
  })) : [];
  
  state.pagination = action.payload.pagination || {
    total: state.queries.length,
    page: 1,
    limit: 10,
    totalPages: 1
  };
  state.error = null;
})

      .addCase(fetchQueries.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.queries = [];
        state.pagination = {
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0
        };
      })

      // Update Query - FIXED VERSION
      .addCase(updateQuery.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateQuery.fulfilled, (state, action) => {
        state.status = "succeeded";
        
        const { id, updatedData, queryData } = action.payload;
        
        if (id && Array.isArray(state.queries)) {
          const index = state.queries.findIndex((query) => query._id === id);
          
          if (index !== -1) {
            // Merge the existing query with the updated data
            state.queries[index] = {
              ...state.queries[index],
              ...updatedData,
              ...queryData
            };
            
            console.log("Query updated in Redux state:", state.queries[index]);
          } else {
            console.warn("Query not found in state for update:", id);
          }
        } else {
          console.warn("Invalid payload in updateQuery.fulfilled:", action.payload);
        }
        
        state.error = null;
      })
      .addCase(updateQuery.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { 
  clearError, 
  resetQueryState, 
  setQueries,
  updateQueryInState // Export the new action
} = queryFormSlice.actions;

// Selectors
export const selectAllQueries = (state) => state.queries.queries;
export const selectQueryById = (state, queryId) => 
  state.queries.queries.find(query => query._id === queryId);
export const selectQueryStatus = (state) => state.queries.status;
export const selectQueryError = (state) => state.queries.error;
export const selectQueryPagination = (state) => state.queries.pagination;
export const selectPendingQueries = (state) => 
  state.queries.queries.filter(query => query.status === 'Pending');
export const selectResolvedQueries = (state) => 
  state.queries.queries.filter(query => query.status === 'Resolved');

export default queryFormSlice.reducer;